// Structured-address value helpers, shared by client (AddressInput + the
// category/wizard renderers) and server (estate-summary, property endpoints).
// Pure — no React, no DOM — so it is safe to import from `server/`.
//
// An `address` field's value is stored as a single string in
// `generic_entries.value`: either a JSON object-string of AddressData, or (for
// a field still typed `text`, or legacy data) a plain free-text address. The
// helpers below tolerate BOTH shapes, which is what lets `formatAddressSummary`
// be applied to a `text` field before its fieldType is flipped to `address`.

export type AddressData = {
  street: string;
  /** Unit / suite / apt — second street line. Added after the original 5 keys;
   *  legacy values without it resolve to "" via the default spread in parse. */
  street2: string;
  city: string;
  state: string;
  postal: string;
  country: string;
};

export const EMPTY_ADDRESS: AddressData = {
  street: "",
  street2: "",
  city: "",
  state: "",
  postal: "",
  country: "",
};

/** The six keys that make a JSON object an ADDRESS. A parsed object must carry at
 *  least one of them; see `parseAddress` for why that test has to exist. */
const ADDRESS_KEYS: readonly (keyof AddressData)[] = [
  "street",
  "street2",
  "city",
  "state",
  "postal",
  "country",
];

/**
 * Parse a stored address value into AddressData, or null when the value is not
 * a JSON address object (empty, a plain-text address, or any non-object JSON).
 * Missing keys (e.g. `street2` on a value saved before it existed) fall back to
 * "" via the EMPTY_ADDRESS spread.
 *
 * 🔴 s202 (D-202.4) — STRICT: the object must carry at least one of the six
 * address keys. This closes a LATENT DEFECT that predates and is independent of
 * any one field type: the old test was `typeof parsed === "object"`, so EVERY
 * JSON object was an address. `{}` parsed as an address. So did any other
 * object-shaped value stored in the same text column, and the six-part join
 * then rendered it as the EMPTY STRING rather than as itself — silently, with
 * no error anywhere. `renderIdentityValue` sits on top of this function, so on
 * an identity field that failure is a record losing its name.
 *
 * Nothing stored is rewritten by the narrowing: a real address still carries
 * address keys and still parses, and a value that stops parsing here was never
 * being rendered correctly in the first place.
 */
export function parseAddress(value: string | null | undefined): AddressData | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const obj = parsed as Record<string, unknown>;
    if (!ADDRESS_KEYS.some((k) => k in obj)) return null;
    return { ...EMPTY_ADDRESS, ...(obj as Partial<AddressData>) };
  } catch {
    return null;
  }
}

/**
 * One-line summary of an address field value. A JSON address object is joined
 * from its non-empty parts in reading order; any other value (plain text,
 * empty) is returned unchanged. The passthrough makes this safe to apply to a
 * `text` field's value before the fieldType flip to `address`.
 */
export function formatAddressSummary(value: string | null | undefined): string {
  if (!value) return "";
  const addr = parseAddress(value);
  if (!addr) return value;
  return [addr.street, addr.street2, addr.city, addr.state, addr.postal, addr.country]
    .filter(Boolean)
    .join(", ");
}

/**
 * The leading segment of a comma-delimited string (everything up to the first
 * comma, trimmed). For a formatted address summary this is the street line, so
 * compact UIs can show "40 Raymond Ave" rather than the full city/postal/country
 * string. A value with no comma is returned trimmed-but-otherwise unchanged.
 */
export function streetSegment(value: string | null | undefined): string {
  if (!value) return "";
  const i = value.indexOf(",");
  return (i >= 0 ? value.slice(0, i) : value).trim();
}
